import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  Grid,
  Typography,
  Autocomplete,
  Divider,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { createFacture } from '../redux/slices/facturesSlice';
import { AppDispatch } from '../redux/store';
import api from '../services/api';

interface FactureFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const FactureForm: React.FC<FactureFormProps> = ({ onSuccess, onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  const [formData, setFormData] = useState({
    client_id: '',
    numero_facture: '',
    date_facture: new Date().toISOString().split('T')[0],
    montant_total: '',
    montant_paye: '0',
    description: '',
    notes: '',
    type_service: '',
    reference_id: '',
  });

  useEffect(() => {
    // Charger les clients
    const fetchClients = async () => {
      try {
        const response = await api.get('/clients', { params: { limit: 1000 } });
        setClients(response.data.data?.clients || []);
      } catch (err) {
        console.error('Erreur chargement clients:', err);
      }
    };
    fetchClients();

    // Générer le numéro de facture
    generateNumeroFacture();
  }, []);

  const generateNumeroFacture = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    const numero = `FAC-${year}${month}${day}-${hours}${minutes}${seconds}`;
    setFormData(prev => ({ ...prev, numero_facture: numero }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (event: any, value: any) => {
    setSelectedClient(value);
    setFormData(prev => ({ ...prev, client_id: value?.id || '' }));
  };

  const getClientLabel = (client: any) => {
    if (client.type_client === 'entreprise') {
      return client.raison_sociale;
    }
    return `${client.nom} ${client.prenom || ''}`.trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.client_id) {
      setError('Veuillez sélectionner un client');
      setLoading(false);
      return;
    }

    if (!formData.montant_total || parseFloat(formData.montant_total) <= 0) {
      setError('Le montant total doit être supérieur à zéro');
      setLoading(false);
      return;
    }

    try {
      await dispatch(createFacture({
        ...formData,
        client_id: parseInt(formData.client_id),
        montant_total: parseFloat(formData.montant_total),
        montant_paye: parseFloat(formData.montant_paye || '0'),
        type_service: formData.type_service || undefined,
        reference_id: formData.reference_id ? parseInt(formData.reference_id) : undefined,
      })).unwrap();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création de la facture');
    } finally {
      setLoading(false);
    }
  };

  const montantRestant = parseFloat(formData.montant_total || '0') - parseFloat(formData.montant_paye || '0');

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Client */}
        <Grid item xs={12}>
          <Autocomplete
            options={clients}
            getOptionLabel={getClientLabel}
            value={selectedClient}
            onChange={handleClientChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                required
                placeholder="Rechercher un client..."
              />
            )}
            noOptionsText="Aucun client trouvé"
          />
        </Grid>

        {/* Numéro facture */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Numéro de facture"
            name="numero_facture"
            value={formData.numero_facture}
            onChange={handleChange}
            helperText="Généré automatiquement, modifiable"
          />
        </Grid>

        {/* Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Date de facturation"
            name="date_facture"
            value={formData.date_facture}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Montants
          </Typography>
        </Grid>

        {/* Montant total */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            type="number"
            label="Montant total"
            name="montant_total"
            value={formData.montant_total}
            onChange={handleChange}
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Montant total de la facture"
          />
        </Grid>

        {/* Montant payé */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Montant payé"
            name="montant_paye"
            value={formData.montant_paye}
            onChange={handleChange}
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Montant déjà encaissé (optionnel)"
          />
        </Grid>

        {/* Montant restant (calculé) */}
        <Grid item xs={12}>
          <Alert severity={montantRestant > 0 ? 'warning' : 'success'}>
            <Typography variant="body2">
              <strong>Montant restant:</strong>{' '}
              {new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0,
              }).format(montantRestant)}
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Informations complémentaires
          </Typography>
        </Grid>

        {/* Type de service */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Type de service"
            name="type_service"
            value={formData.type_service}
            onChange={handleChange}
            helperText="Optionnel - pour lier à un service"
          >
            <MenuItem value="">Aucun</MenuItem>
            <MenuItem value="demarche">Démarche</MenuItem>
            <MenuItem value="reparation">Réparation</MenuItem>
            <MenuItem value="solution_digitale">Solution digitale</MenuItem>
            <MenuItem value="service_divers">Service divers</MenuItem>
          </TextField>
        </Grid>

        {/* ID de référence */}
        {formData.type_service && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="ID de référence"
              name="reference_id"
              value={formData.reference_id}
              onChange={handleChange}
              inputProps={{ min: 1 }}
              helperText={`ID du ${formData.type_service}`}
            />
          </Grid>
        )}

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Détails de la facture..."
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes internes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Notes privées..."
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Création...' : 'Créer la facture'}
        </Button>
      </Box>
    </Box>
  );
};

export default FactureForm;
